<!--
  滚动加载
-->
<template>
  <div
    :class="'utm-scroll-list ' + className"
    :style="{'height': height, 'width': width }"
    @scroll="handleScrollDom"
    ref="scrollList"
  >
    <template v-if="transition">
      <transition-group name="utm-scroll" tag="div" 
        class="utm-scroll-list-container" 
        :class="{'utm-scroll-list-no-top-padding': startIndex === 0 && !isPageSearchMode && !noPadding }"
        :style="{'padding': showScrollBar ? SCROLL_PADDING + 'px 0' :SCROLL_PADDING_NO_BAR + 'px 0'}">
        <div v-for="(item,index) in list" :key="item[ itemKey ] || index" :class="itemClassName" :style="itemStyle">
          <slot :row="item" :index="startIndex + index"></slot>
        </div>
      </transition-group>
    </template>
    <template v-else>
      <transition-group name="utm-scroll-no-anim" tag="div" 
        class="utm-scroll-list-container" 
        :class="{'utm-scroll-list-no-top-padding': startIndex === 0 && !isPageSearchMode && !noPadding}"
        :style="{'padding': showScrollBar ? SCROLL_PADDING + 'px 0' :SCROLL_PADDING_NO_BAR + 'px 0'}"
      >
        <div v-for="(item,index) in list" :key="item[ itemKey ] || index" :class="itemClassName" :style="itemStyle">
          <slot :row="item" :index="startIndex + index"></slot>
        </div>
      </transition-group>
    </template>
    <slot name="footer"></slot>
  </div>
</template>

<script>
  /**
   * @props:
   *    描述见props选项部分;
   * @events:
   *    'on-top': 滚动到最顶部列表的第一条数据;
   *    'on-bottom': 滚动到最底部列表的最后一条数据;
   * @methods:
   *    getScrollTop: 获取滚动距离;
   *    scrollTo: 垂直滚动到指定位置;
   *    scrollToTop: 滚动到顶部;
   *    scrollToBottom: 滚动到底部最后一条数据;
   *    resetRender: 当isPageSearchMode属性为true, data属性值改变时,不会重新渲染第一条；如果需要重新渲染顶部第一条(比如改变搜索条件，重置搜索)，调用该方法;
   *    setStartRenderIndex: 设置开始渲染的元素下标;
   * */
  export default{
    name: 'utmScrollList',
    props:{
      /**
       * 是否是接口分页查询模式
       * 如果为true, 'on-bottom'事件触发，处理该事件进行分页查询后，需将下页的数据push进data数组，data改变后不会重新到顶部第一条数据(分页模式renderCount的值需要小于等于每页记录数量);
       * 如果需要重新渲染顶部第一条(比如改变搜索条件，重置搜索)，需调用resetRender方法渲染第一条;
       * 如果为false，则data改变后，会重新渲染顶部第一条数据;
       */
      isPageSearchMode: {
        type: Boolean,
        default: false
      },
      // 当源数据变化时是否显示第一页
      resetStartIndexWhenDataChange: {
        type: Boolean,
        default: true
      },
      // 不需要padding
      noPadding:{
         type:Boolean,
         default:false
      },
      // 启用过渡
      transition: {
        type: Boolean,
        default: true
      },
      // 高度，css height属性
      height:{
        type: String,
        default: "200px"
      },
      // 宽度，css width属性
      width:{
        type: String,
        default: "100%"
      },
      // 数据列表
      data: {
        type: Array,
        default(){
          return [];
        }
      },
      // 总共渲染的数量
      renderCount: {
        type: Number,
        default: 20
      },
      // 每次滚动加载的数量
      loadCount: {
        type: Number,
        default: 3
      },
      // vue过渡动画的key使用的键名，默认为id
      itemKey: {
        type: String,
        default: 'id'
      },
      itemClassName: {
        type: String,
        default: ''
      },
      // 子级样式对象
      itemStyle: {
        type: Object,
        default(){
          return {};
        }
      },
      className:{
        type: String,
        default: ''
      },
      /*因为有的页面不需要22px的间距，所以改成了可传的参数*/
      scrollPadding:{
        type: Number,
        default: 22
      },
      //并且有的页面不希望挨在一起 search和list
      SCROLL_PADDING_NO_BAR:{
        type:Number,
        default:0
      },
      addTop:{
        type:Number,
        default:0
      },
      // 在QR code模块发现当翻页到其他非1页面的时候，如果搜索某个数据会展示不出来，初步判断是list computed函数中切割失误，
      //通过这个参数判断如果data.length为1的时候修改startIndex为0
      isToStartIndexOneWhenDataLengthWaiteOne:{
        type:Boolean,
        default:false
      }
    },
    watch: {
      // 数据变化
      data: {
        handler(nv) {
          if (!nv || !nv.length) {
            this.isReset = true;
          }
          this.handleDataChange();
        }
      },
      // 需要渲染的数量变化
      renderCount: {
        handler(nv){
          this.maxStartIndex = Math.max( 0, this.data.length - this.renderCount );
        }
      },
    },
    computed:{
      // 筛选出要渲染的列表部分
      list(){
        if(this.data.length == 1 && this.isToStartIndexOneWhenDataLengthWaiteOne){
          this.startIndex = 0
        }
        let endIndex = this.startIndex + this.renderCount;
        let arr = this.data.slice(this.startIndex, endIndex );
        return arr;
      },
      showScrollBar(){
        return this.data.length >= this.renderCount;
      }
    },
    data(){
      return {
        startIndex: 0,
        maxStartIndex: 0,
        SCROLL_PADDING: this.scrollPadding,
        isReset: true,
        isScrollToBottom: false,
        topScrollTimer: null,
        bottomScrollTimer: null,
        scrollTimer: null,
      }
    },
    mounted(){
      this.handleDataChange();
    },
    beforeDestroy(){
      clearTimeout(this.topScrollTimer);
      clearTimeout(this.bottomScrollTimer);
      clearTimeout(this.scrollTimer);
    },
    methods:{
      // 设置开始渲染的元素下标
      setStartRenderIndex: function(i){
        let max = this.maxStartIndex;
        if (i > max) {
          i = max;
        }
        this.startIndex = i;
      },
      // 获取垂直滚动距离
      getScrollTop: function(){
        return this.$refs.scrollList.scrollTop;
      },
      // 滚动到指定位置
      scrollTo: function(value){
        this.$refs.scrollList.scrollTop = value;
      },
      // 滚动到顶部
      scrollToTop: function(){
        this.$refs.scrollList.scrollTop = this.SCROLL_PADDING;
      },
      // 滚动到底部
      scrollToBottom: function(){
        console.log(1);
        this.$nextTick(()=>{
          this.startIndex = this.maxStartIndex;
          let target = this.$refs.scrollList;
          this.$nextTick(()=>{
            this.$refs.scrollList.scrollTop = target.scrollHeight - target.clientHeight - this.SCROLL_PADDING;
          });
        });
      },
      // 处理数据变化
      handleDataChange: function(){
        let scrollReservedVal = Math.max(2, this.SCROLL_PADDING - 2);
        // 不是分页模式，重新渲染第一条
        if (!this.isPageSearchMode) {
          if (this.resetStartIndexWhenDataChange) {
            this.startIndex = 0;
          }
          this.maxStartIndex = Math.max( 0, this.data.length - this.renderCount );
          this.isReset = false;
        // 分页渲染模式，并且不是初始化，渲染下几条
        }else if( !this.isReset ){
          let maxStartIndex = Math.max( 0, this.data.length - this.renderCount );
          let startIndex = this.startIndex;
          this.maxStartIndex = maxStartIndex;
          let target = this.$refs.scrollList;
          if( startIndex < maxStartIndex ){
            // 滚动位置调整
            clearTimeout(this.scrollTimer);
            this.scrollTimer = setTimeout(()=>{
              if (target.scrollTop < scrollReservedVal) {
                target.scrollTop = scrollReservedVal;
              } else if (target.scrollTop > target.scrollHeight - target.clientHeight - scrollReservedVal ) {
                target.scrollTop = target.scrollHeight - target.clientHeight - scrollReservedVal;
              }
            }, 50);
          }
        }else{
          this.maxStartIndex = Math.max( 0, this.data.length - this.renderCount );
          this.isReset = false;
        }
      },
      // 重置渲染
      resetRender: function(){
        this.startIndex = 0;
        this.isScrollToBottom = false;
        this.maxStartIndex = Math.max( 0, this.data.length - this.renderCount );
      },
      // 设置是否滚动到底部
      setIsScrollToBottom: function(val){
        this.isScrollToBottom = val;
      },
      // 处理滚动元素
      handleScrollDom: function(e){
        let target = e.target;
        let startIndex = this.startIndex;
        let loadCount = this.loadCount;
        let maxStartIndex = this.maxStartIndex;
        let scrollReservedVal = Math.max(2, this.SCROLL_PADDING - 2);
        let isUpdateOver = false;
        let updated = false;
        if( target.scrollTop === 0 ){
          if (this.bottomScrollTimer || this.topScrollTimer) {
            return;
          }
          updated = true;
          // 向上加载
          if( startIndex > 0 ){
            /**
             * 如果开始序数不能被加载数量整除,取比当前开始序数小的最大能被整除的数作为开始序数（用于优化二维布局）；
             */
            let reveredNum = startIndex % loadCount;
            if (reveredNum > 0) {
              this.startIndex = startIndex - reveredNum;
            }else{
              let changeCount = Math.min( startIndex, loadCount );
              this.startIndex = startIndex - changeCount;
            }
            // 防止连续触发
            this.topScrollTimer = setTimeout(()=>{
              this.topScrollTimer = null;
            },100);
          }else if (this.isPageSearchMode) {
            this.$emit('on-top');
            updated = false;
          }
          if (startIndex <= 0 && !this.isPageSearchMode) {
            isUpdateOver = true;
          }
        }else if( target.scrollTop + 1 + this.addTop>= target.scrollHeight - target.clientHeight ){ // 多加1为上下margin
          updated = true;
          // 向下加载
          if( startIndex < maxStartIndex ){
            /**
             * 由于改变视图（this.startIndex改变）会导致数据列表变化，
             * 进而在视图改变过程中造成触发滚动到顶部的事件（此时scrollTop为0），
             * 造成显示数据异常的问题，
             * 因此这儿设置定时器防止误触发滚动到顶部事件
             * */
            clearTimeout(this.bottomScrollTimer);
            this.bottomScrollTimer = setTimeout(()=>{
              this.bottomScrollTimer = null;
            }, 300);

            let changeCount = loadCount;
            this.startIndex = startIndex + changeCount;
          } else if (this.isPageSearchMode) {
            this.$emit('on-bottom', ()=>{
              let changeCount = this.loadCount;
              this.startIndex = startIndex + changeCount;
            });
          }
          if ( (this.startIndex >= this.maxStartIndex && !this.isPageSearchMode) || this.isScrollToBottom ) {
            isUpdateOver = true;
          }
        }
        // 滚动位置调整
        if (!isUpdateOver && updated) {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(()=>{
            if (target.scrollTop < scrollReservedVal && this.addTop == 0) {
              target.scrollTop = scrollReservedVal;
            } else if (target.scrollTop > target.scrollHeight - target.clientHeight - scrollReservedVal && this.addTop == 0) {
              target.scrollTop = target.scrollHeight - target.clientHeight - scrollReservedVal;
            }
          }, 50);
        }
      },
    }
  }
</script>

<style>
  .utm-scroll-list{
    box-sizing: content-box;
    width: 300px;
    overflow: auto;
    overflow-x: hidden;
  }
  .utm-scroll-list::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: #303745;
  }
  .utm-scroll-list::-webkit-scrollbar-track {
    border-radius: 3px;
    background-color: rgba(255, 255, 255, 0.9);
  }
  .utm-scroll-list::-webkit-scrollbar-thumb {
    border-radius: 3px;
    box-shadow: inset 0 0 6px rgba(0, 0, 0, .3);
    background-color: #B4B4B4;
  }
  .utm-scroll-list-container{
    box-sizing: content-box;
    min-height: 100%;
  }
  .utm-no-scroll::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    background-color: #303745;
  }

  .utm-scroll-list-no-top-padding {
    padding-top: 0 !important;
  }
  /* 过渡 */
  .utm-scroll-enter{
    opacity: 0;
  }
  .utm-scroll-enter-active{
    transition: all 0.4s;
  }
  .utm-scroll-leave,
  .utm-scroll-leave-active,
  .utm-scroll-leave-to{
    opacity: 0;
  }
  /* 没有过渡 */
  .utm-scroll-no-anim-enter-active{
    transition: all 0.4s;
  }
</style>



// WEBPACK FOOTER //
// src/admin/components/commonComponents/scrollLoadList.vue